// ABOUTME: Confirmation dialog for removing a family member
// ABOUTME: Shows member details, warns about restrictions, and confirms removal action

import React from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Alert
} from '@mui/material';
import { useFamilyTerminology } from '../../../hooks/useFamilyTerminology';
import type { FamilyMember } from '../types';
import { canRemovePrimaryCaregiver, shouldShowPrimaryCaregiverWarning } from '../../../utils/familyPermissions';

interface RemoveMemberDialogProps {
  open: boolean;
  member: FamilyMember | null;
  familyMembers: FamilyMember[];
  removing: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const RemoveMemberDialog: React.FC<RemoveMemberDialogProps> = ({
  open,
  member,
  familyMembers,
  removing,
  onClose,
  onConfirm
}) => {
  const { term, Term } = useFamilyTerminology();

  if (!member) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="remove-member-confirmation"
    >
      <DialogTitle>Remove {Term} Member</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to remove <strong>{member.firstName} {member.lastName}</strong> from your {term}?
        </Typography>
        <Typography sx={{ mt: 2, color: 'text.secondary' }}>
          This will revoke their access to the family dashboard and prevent them from recording behaviors,
          but will preserve all behavior records they created for historical accuracy.
        </Typography>
        {shouldShowPrimaryCaregiverWarning(member.userId, familyMembers) && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>This family member cannot be removed.</strong>
            <br />
            Possible reasons:
            <Box component="ul" sx={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
              <li>They are the last Primary Caregiver (at least one is required)</li>
              <li>They are the billing owner of an active subscription</li>
            </Box>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={removing}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          disabled={!canRemovePrimaryCaregiver(member.userId, familyMembers) || removing}
        >
          {removing ? <CircularProgress size={24} /> : 'Remove Member'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveMemberDialog;
